/**
 * The attempt view (LIS-1173, the purchase design's §2.6): what a running
 * vendor purchase attempt shows, compiled by the server from the attempt
 * and sent with its status and with each answer. Clients draw it as it
 * arrives and never read the attempt's phase. Every union is closed.
 */
import { z } from "zod";
import { answersSchema, sectionSchema } from "./purchase";
import { storeCurrencySchema } from "@libs/buy-on-behalf-contracts/vendor-checkout";

/** The ways a seller sends a code again. */
export const resendChannels = ["sms", "email", "call"] as const;

/** A button the view offers, of a closed set the server decides for the
 *  moment. */
export const attemptActionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("stop"), label: z.string().min(1).max(60) }).strict(),
  // No longer sent (28 Oct 2026): a browser's stop is never the buyer's.
  // Kept so a client parses a server that still sends it.
  z.object({ kind: z.literal("stop_again"), label: z.string().min(1).max(60) }).strict(),
  z.object({ kind: z.literal("back_to_checkout"), label: z.string().min(1).max(60) }).strict(),
  z.object({ kind: z.literal("reselect"), label: z.string().min(1).max(60) }).strict(),
  z.object({ kind: z.literal("checked_email"), label: z.string().min(1).max(60) }).strict(),
  z.object({ kind: z.literal("wallet"), label: z.string().min(1).max(60), url: z.url() }).strict(),
  z.object({ kind: z.literal("continue_on_seller"), label: z.string().min(1).max(60), url: z.url() }).strict(),
  z.object({ kind: z.literal("resend"), label: z.string().min(1).max(60), id: z.enum(resendChannels) }).strict(),
  // A bank's check that has gone unanswered closed by the buyer: the
  // purchase ends not bought (answered `close_bank_check`).
  z.object({ kind: z.literal("close_bank_check"), label: z.string().min(1).max(60) }).strict(),
]);
export type AttemptAction = z.infer<typeof attemptActionSchema>;

/** Where the live view of the seller's page connects: Browser Use's CDP
 *  host, or a Boat browser's live view's gated address. */
export const liveViewHostSchema = z.string().min(1).max(253);

/** What the view shows under its status. */
export const attemptScreenSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("progress") }).strict(),
  z
    .object({
      kind: z.literal("ask"),
      /** The step's sequence: an answer names it. */
      sequence: z.number().int().positive(),
      hint: z.string().min(1).max(400),
      sections: z.array(sectionSchema).min(1).max(2),
      submit: z.string().min(1).max(60),
      /** An answer was given and the seller has not read it yet. */
      answered: z.boolean(),
      /** The last answer was refused. */
      retry: z.boolean(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("confirm_total"),
      sequence: z.number().int().positive(),
      hint: z.string().min(1).max(400),
      totalMinor: z.number().int().nonnegative(),
      taxMinor: z.number().int().nonnegative(),
      currency: storeCurrencySchema,
      accept: z.string().min(1).max(60),
      refuse: z.string().min(1).max(60),
      answered: z.boolean(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("live_view"),
      host: liveViewHostSchema,
      check: z.enum(["bank", "human"]),
      hint: z.string().min(1).max(400),
      /** The check may need typing (a bank's one-time code). */
      keyboard: z.boolean(),
    })
    .strict(),
  z.object({ kind: z.literal("outcome"), result: z.enum(["bought", "not_bought", "uncertain", "stopped"]) }).strict(),
]);
export type AttemptScreen = z.infer<typeof attemptScreenSchema>;

export const attemptViewSchema = z
  .object({
    id: z.uuid(),
    /** The attempt's latest progress sequence. */
    sequence: z.number().int().positive(),
    /** Running; stopping on the buyer's ask; `stop_unconfirmed`, no longer
     *  sent (28 Oct 2026) and kept so a client parses a server that still
     *  sends it; confirming:
     *  Pay was pressed and nothing more is asked of the buyer while the
     *  seller's answer is confirmed (its page, then its order email, order
     *  page or API): the buyer may leave unasked and the view still changes;
     *  ended. */
    lifecycle: z.enum(["running", "stopping", "stop_unconfirmed", "confirming", "ended"]),
    /** Pay was clicked: the card may have been charged. */
    payClaimed: z.boolean(),
    status: z
      .object({ words: z.string().min(1).max(200), mark: z.enum(["spinner", "done", "ready", "stopped"]) })
      .strict(),
    /** Under the status, at any lifecycle. */
    note: z.string().max(600).nullable(),
    screen: attemptScreenSchema,
    offer: z
      .object({
        lines: z
          .array(
            z
              .object({
                name: z.string().min(1).max(240),
                quantity: z.number().int().positive(),
                totalMinor: z.number().int().nonnegative(),
              })
              .strict(),
          )
          .min(1),
        totalMinor: z.number().int().nonnegative(),
        taxMinor: z.number().int().nonnegative().nullable(),
        currency: storeCurrencySchema,
        /** The delivery the order pinned, its fee in `totalMinor`: the
         *  store's option by the name it gives it ("Amazon - Premium"), or
         *  the listing's own postage ("Delivery"), at its fee in minor units
         *  (0 for postage that costs nothing); null for goods that are not
         *  posted (tickets). `dutiesDue` is whether it leaves import duties
         *  to the buyer ("Import duties may be due on delivery", said beside
         *  the postage, a resumed checkout included): the store option's own
         *  terms, else the listing's saved country facts; false where
         *  nothing says so. */
        delivery: z
          .object({
            name: z.string().min(1).max(240),
            feeMinor: z.number().int().nonnegative(),
            dutiesDue: z.boolean(),
          })
          .strict()
          .nullable(),
      })
      .strict(),
    actions: z.array(attemptActionSchema).max(4),
  })
  .strict();
export type AttemptView = z.infer<typeof attemptViewSchema>;

/**
 * The buyer's answer to the attempt's current screen
 * (`POST attempts/:id/answer`), naming the step it answers (`sequence`):
 * the fields an `ask` shows, a code sent again another way, the taxed
 * total accepted or refused, or a bank's check that has gone unanswered
 * closed (its view's `close_bank_check` action).
 */
export const attemptAnswerSchema = z
  .object({
    sequence: z.number().int().positive(),
    answer: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("ask"), answers: answersSchema }).strict(),
      z.object({ kind: z.literal("resend"), id: z.enum(resendChannels) }).strict(),
      z.object({ kind: z.literal("confirm_total"), accept: z.boolean() }).strict(),
      z.object({ kind: z.literal("close_bank_check") }).strict(),
    ]),
  })
  .strict();
export type AttemptAnswer = z.infer<typeof attemptAnswerSchema>;

/**
 * A start the server refused, in words it built (§2.5): its code, a title
 * and a sentence for the buyer, the field to go back to where it is one of
 * the purchase's, and each problem with the answers.
 */
export const startRefusalSchema = z
  .object({
    code: z.string().min(1).max(60),
    title: z.string().min(1).max(120),
    message: z.string().min(1).max(400),
    fieldId: z.string().max(60).nullable(),
    problems: z
      .array(
        z
          .object({
            fieldId: z.string().min(1).max(60),
            part: z.enum(["line1", "line2", "city", "region", "postcode", "country"]).nullable(),
            code: z.string().min(1).max(60),
          })
          .strict(),
      ),
  })
  .strict();
export type StartRefusal = z.infer<typeof startRefusalSchema>;
